import { CheckOutlined, CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { faPencil, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { TableColumnsType } from "antd";
import { Button, Form, Input, Popconfirm, Space, Table, Tooltip, Typography, message } from "antd";
import React from "react";

export type DomainDataValues = {
    code: string;
    description: string;
};

export type DomainDataCreateValues = DomainDataValues;

export type DomainDataChanges = Partial<DomainDataValues>;

export type DomainDataEditPermission = {
    canAdd: boolean;
    canEdit: boolean;
    canDelete: boolean;
};

export type DomainDataMutationResult = {
    operation?: "create" | "update" | "delete";
    inProgress: boolean;
    success?: boolean;
    code?: string;
    message?: string;
    fields?: Partial<Record<keyof DomainDataValues, string>>;
    outcomeUncertain?: boolean;
    dataDomainId?: string;
    recordId?: string | number;
};

export interface DomainDataEditTableProps {
    dataDomainId?: string;
    items?: IDomainDataResultItem[];
    recordCount?: number;
    loading?: boolean;
    readOnly?: boolean;
    permission?: DomainDataEditPermission;
    mutationResult?: DomainDataMutationResult;
    onAdd: (values: DomainDataCreateValues) => void | Promise<void>;
    onEdit: (record: IDomainDataResultItem, values: DomainDataChanges) => void | Promise<void>;
    onDelete: (record: IDomainDataResultItem) => void | Promise<void>;
    onPageSelect?: (pagination: IPagination) => void;
}

type EditableDomainDataItem = IDomainDataResultItem & {
    temporaryKey?: string;
    isNew?: boolean;
};

const getRecordKey = (record: EditableDomainDataItem): React.Key => record.temporaryKey ?? record.domainDataId ?? `code-${record.code}`;

export default function DomainDataEditTable({
    dataDomainId,
    items = [],
    recordCount,
    loading = false,
    readOnly = false,
    permission,
    mutationResult,
    onAdd,
    onEdit,
    onDelete,
    onPageSelect,
}: DomainDataEditTableProps) {
    const [form] = Form.useForm<DomainDataValues>();
    const [editingKey, setEditingKey] = React.useState<React.Key | null>(null);
    const [newRecord, setNewRecord] = React.useState<EditableDomainDataItem | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [deletingKey, setDeletingKey] = React.useState<React.Key | null>(null);
    const previousMutationResult = React.useRef(mutationResult);

    const rows = React.useMemo(() => (newRecord ? [newRecord, ...items] : items), [items, newRecord]);

    const cancelEdit = React.useCallback(() => {
        setEditingKey(null);
        setNewRecord(null);
        form.resetFields();
    }, [form]);

    React.useEffect(() => {
        if (!mutationResult || mutationResult === previousMutationResult.current) {
            return;
        }
        previousMutationResult.current = mutationResult;
        if (mutationResult.dataDomainId && dataDomainId && mutationResult.dataDomainId !== dataDomainId) {
            return;
        }
        if (mutationResult.inProgress) {
            return;
        }

        if (mutationResult.operation === "delete") {
            setDeletingKey(null);
        }
        if (mutationResult.success) {
            if (mutationResult.operation !== "delete") {
                cancelEdit();
            }
            setSaving(false);
            return;
        }

        if (mutationResult.fields) {
            form.setFields(
                Object.entries(mutationResult.fields).map(([name, errors]) => ({
                    name: name as keyof DomainDataValues,
                    errors: errors ? [errors] : [],
                })),
            );
        } else if (mutationResult.code === "DUPLICATE_DOMAIN_DATA_CODE") {
            form.setFields([{ name: "code", errors: [mutationResult.message || "This key already exists."] }]);
        }

        const discardStaleEdit =
            mutationResult.outcomeUncertain ||
            mutationResult.code === "DOMAIN_DATA_VERSION_CONFLICT" ||
            mutationResult.code === "DOMAIN_DATA_NOT_FOUND" ||
            mutationResult.code === "DATA_DOMAIN_NOT_FOUND";
        if (discardStaleEdit) {
            cancelEdit();
        } else {
            setSaving(false);
        }
    }, [cancelEdit, dataDomainId, form, mutationResult]);

    const addRecord = React.useCallback(() => {
        const temporaryRecord: EditableDomainDataItem = {
            temporaryKey: `new-${Date.now()}`,
            isNew: true,
            code: "",
            description: "",
        };
        setNewRecord(temporaryRecord);
        setEditingKey(getRecordKey(temporaryRecord));
        form.setFieldsValue({ code: "", description: "" });
    }, [form]);

    const editRecord = React.useCallback(
        (record: EditableDomainDataItem) => {
            setEditingKey(getRecordKey(record));
            form.setFieldsValue({ code: record.code, description: record.description });
        },
        [form],
    );

    const saveRecord = React.useCallback(
        async (record: EditableDomainDataItem) => {
            try {
                const formValues = await form.validateFields();
                const values: DomainDataValues = {
                    code: formValues.code.trim(),
                    description: formValues.description.trim(),
                };
                const duplicate = items.some((item) => item.domainDataId !== record.domainDataId && item.code === values.code);
                if (duplicate) {
                    form.setFields([{ name: "code", errors: ["This key already exists in the loaded records."] }]);
                    return;
                }

                setSaving(true);
                if (record.isNew) {
                    const result = onAdd(values);
                    if (!mutationResult) {
                        await result;
                        cancelEdit();
                        setSaving(false);
                    }
                } else {
                    if (record.domainDataId == null || record.version == null) {
                        throw new Error("This record is missing the ID or version required for editing. Refresh the records and try again.");
                    }
                    const changes: DomainDataChanges = {};
                    if (record.code !== values.code) {
                        changes.code = values.code;
                    }
                    if (record.description !== values.description) {
                        changes.description = values.description;
                    }
                    if (Object.keys(changes).length > 0) {
                        const result = onEdit(record, changes);
                        if (!mutationResult) {
                            await result;
                            cancelEdit();
                            setSaving(false);
                        }
                    } else {
                        cancelEdit();
                    }
                }
            } catch (error) {
                if (!(error && typeof error === "object" && "errorFields" in error)) {
                    message.error(error instanceof Error ? error.message : "Unable to save the record.");
                }
                setSaving(false);
            }
        },
        [cancelEdit, form, items, mutationResult, onAdd, onEdit],
    );

    const deleteRecord = React.useCallback(
        async (record: EditableDomainDataItem) => {
            const recordKey = getRecordKey(record);
            setDeletingKey(recordKey);
            try {
                if (record.domainDataId == null) {
                    throw new Error("This record is missing the ID required for deletion. Refresh the records and try again.");
                }
                const result = onDelete(record);
                if (!mutationResult) {
                    await result;
                    setDeletingKey(null);
                }
            } catch (error) {
                message.error(error instanceof Error ? error.message : "Unable to delete the record.");
                setDeletingKey(null);
            }
        },
        [mutationResult, onDelete],
    );

    const renderField = React.useCallback(
        (field: keyof DomainDataValues, record: EditableDomainDataItem) => {
            if (editingKey !== getRecordKey(record)) {
                return record[field];
            }

            return (
                <Form.Item
                    name={field}
                    className="mb-0"
                    rules={[
                        {
                            required: true,
                            transform: (value: string) => value?.trim(),
                            message: `Please enter a ${field === "code" ? "key" : "description"}.`,
                        },
                        {
                            whitespace: true,
                            transform: (value: string) => value?.trim(),
                            message: `Please enter a ${field === "code" ? "key" : "description"}.`,
                        },
                        {
                            max: 500,
                            transform: (value: string) => value?.trim(),
                            message: `${field === "code" ? "Key" : "Description"} cannot exceed 500 characters.`,
                        },
                    ]}
                >
                    <Input autoFocus={field === "code"} aria-label={field === "code" ? "Key" : "Description"} disabled={saving} onPressEnter={() => saveRecord(record)} />
                </Form.Item>
            );
        },
        [editingKey, saveRecord, saving],
    );

    const interactionDisabled = loading || (mutationResult?.inProgress ?? false);

    const columns = React.useMemo<TableColumnsType<EditableDomainDataItem>>(() => {
        const canAdd = !readOnly && (permission?.canAdd ?? true);
        const canEdit = !readOnly && (permission?.canEdit ?? true);
        const canDelete = !readOnly && (permission?.canDelete ?? true);
        const result: TableColumnsType<EditableDomainDataItem> = [
            {
                title: "No.",
                key: "index",
                width: 70,
                render: (_value, record, index) => (record.isNew ? "New" : (record.sNo ?? index + 1)),
            },
            {
                title: "Key",
                dataIndex: "code",
                key: "code",
                width: "35%",
                render: (_value, record) => renderField("code", record),
            },
            {
                title: "Description",
                dataIndex: "description",
                key: "description",
                render: (_value, record) => renderField("description", record),
            },
        ];

        if (canAdd || canEdit || canDelete) {
            result.push({
                title: "Actions",
                key: "actions",
                width: 120,
                render: (_value, record) => {
                    const recordKey = getRecordKey(record);
                    const isEditing = editingKey === recordKey;
                    if (isEditing) {
                        return (
                            <Space size="small">
                                <Tooltip title="Save">
                                    <Button type="text" aria-label="Save record" icon={<CheckOutlined />} loading={saving} onClick={() => saveRecord(record)} />
                                </Tooltip>
                                <Tooltip title="Cancel">
                                    <Button type="text" aria-label="Cancel editing" icon={<CloseOutlined />} disabled={saving} onClick={cancelEdit} />
                                </Tooltip>
                            </Space>
                        );
                    }

                    return (
                        <Space size="small">
                            {canEdit && (
                                <Tooltip title="Edit">
                                    <Button
                                        type="link"
                                        aria-label={`Edit ${record.code}`}
                                        icon={<FontAwesomeIcon icon={faPencil} />}
                                        disabled={editingKey !== null || deletingKey !== null || interactionDisabled}
                                        onClick={() => editRecord(record)}
                                    />
                                </Tooltip>
                            )}
                            {canDelete && (
                                <Popconfirm
                                    title="Delete record?"
                                    description={`Delete “${record.code}”?`}
                                    okText="Delete"
                                    okButtonProps={{ danger: true, loading: deletingKey === recordKey }}
                                    onConfirm={() => deleteRecord(record)}
                                >
                                    <Tooltip title="Delete">
                                        <Button
                                            type="link"
                                            danger
                                            aria-label={`Delete ${record.code}`}
                                            icon={<FontAwesomeIcon icon={faTrashCan} />}
                                            loading={deletingKey === recordKey}
                                            disabled={editingKey !== null || interactionDisabled || (deletingKey !== null && deletingKey !== recordKey)}
                                        />
                                    </Tooltip>
                                </Popconfirm>
                            )}
                        </Space>
                    );
                },
            });
        }

        return result;
    }, [cancelEdit, deleteRecord, deletingKey, editRecord, editingKey, interactionDisabled, permission, readOnly, renderField, saveRecord, saving]);

    const canAdd = !readOnly && (permission?.canAdd ?? true);

    return (
        <Form form={form} component={false}>
            <div className="flex size-full flex-1 flex-col gap-3 overflow-hidden">
                <div className="flex items-center justify-between gap-4">
                    <Typography.Text type="secondary">Number of records: {recordCount ?? items.length}</Typography.Text>
                    {canAdd && (
                        <Button type="primary" icon={<PlusOutlined />} disabled={editingKey !== null || deletingKey !== null || interactionDisabled} onClick={addRecord}>
                            Add record
                        </Button>
                    )}
                </div>
                <Table<EditableDomainDataItem>
                    data-testid="domain-data-edit-table"
                    className="min-h-0 flex-1 overflow-auto"
                    dataSource={rows}
                    columns={columns}
                    rowKey={getRecordKey}
                    loading={loading}
                    size="small"
                    scroll={{ x: 650 }}
                    pagination={{
                        hideOnSinglePage: true,
                        pageSize: 100,
                        total: Math.max(recordCount ?? rows.length, rows.length),
                        showSizeChanger: false,
                        showTotal: (total, range) => `${range[0]} - ${range[1]} of total ${total}`,
                    }}
                    onChange={(pagination, _filters, _sorter, extra) => {
                        if (extra.action === "paginate" && onPageSelect) {
                            onPageSelect(pagination as IPagination);
                        }
                    }}
                    locale={{ emptyText: "No domain data records. Add the first record to get started." }}
                />
            </div>
        </Form>
    );
}
